import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  cn,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  SatirDugmesi,
  SatirMetni,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  gorunumBitir,
  gorunumDurumu,
  gorunumAdaylari,
  gorunumeGec,
  type GorunumDurumu,
} from "./api";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";

/**
 * KULLANICI GORUNUMU — F-AUTH-07
 *
 * Yetkili, destek amaciyla bir kullanicinin gozunden sistemi gorur.
 * Kurallar sunucuda (`auth/gorunum.ts`); bu dosya yalnizca ekran.
 */

interface Aday {
  id: string;
  ad: string;
  roller: string[];
}

/** Durumu okuyan ortak kanca — hem serit hem ekran kullaniyor. */
export function useGorunumDurumu() {
  return useQuery<GorunumDurumu>({
    queryKey: ["gorunum-durumu"],
    queryFn: gorunumDurumu,
    // Baska sekmede cikildiysa bu sekme de fark etsin.
    refetchOnWindowFocus: true,
    retry: false,
  });
}

// =====================================================================
// UYARI SERIDI
// =====================================================================

/**
 * HER SAYFANIN USTUNDE DURAN SERIT
 *
 * ================================================================
 * KULLANICI HANGI GOZLE BAKTIGINI UNUTMAMALI
 * ================================================================
 * Gorunum modunun en sinsi riski yetki degil, YANLIS OKUMA: yonetici
 * bir uzmanin ekranini kendi ekrani sanip "neden bu ogrenciler
 * gorunmuyor" diye bir arizanin pesine duser, ya da daha kotusu,
 * gordugu eksik listeye bakip yanlis bir karar verir.
 *
 * Bu yuzden serit kapatilamiyor, sayfa kaydirilinca da yerinde
 * kaliyor ve panelin geri kalanindan AYRI bir renk tasiyor. Rahatsiz
 * edici olmasi kasitli — gorunum modu normal bir calisma hali degil.
 */
export function GorunumSeridi() {
  const qc = useQueryClient();
  const { data } = useGorunumDurumu();
  /** `null` = SÜRESİZ görünüm (F-AUTH-07). Sıfır DEĞİL — sıfır "süresi doldu" demek. */
  const [kalan, setKalan] = useState<number | null>(null);

  const cik = useMutation({
    mutationFn: gorunumBitir,
    // HER SEY tazeleniyor: ekrandaki butun veriler hedefin gozunden
    // gelmisti ve artik gecersiz.
    onSettled: () => void qc.invalidateQueries(),
  });

  /*
    SURESIZ GORUNUM: `kalanSaniye === null` (F-AUTH-07, 05.09.2026).

    `?? 0` YAZILAMAZ: sifir "suresi doldu" demek ve sayac o an
    "bitti" diye sorgu tazeler — yani suresiz bir gorunum saniyede bir
    kendini kapatmaya calisirdi. `null`u `null` olarak tasiyoruz.
  */
  useEffect(() => {
    setKalan(data?.kalanSaniye ?? null);
  }, [data?.kalanSaniye, data?.gorunumId]);

  /**
   * Geri sayim YEREL, dogruluk SUNUCUDA.
   *
   * Saniyede bir sunucuya sormak gereksiz yuk; ama yerel sayac sifira
   * indiginde ONA GUVENIP "bitti" demiyoruz — sorgu tazeleniyor ve
   * kararı sunucu veriyor. Sure zaten `expiresAt` ile veritabaninda:
   * bu sayac yalnizca bir gostergedir.
   */
  useEffect(() => {
    if (!data?.aktif) return;
    // Suresiz gorunumde sayilacak bir sey yok.
    if (kalan === null) return;
    const s = setInterval(() => {
      setKalan((k) => {
        // Suresiz gorunum sayaci hic baslatmiyor; yine de tip guvenligi
        // icin: `null` gelirse dokunma.
        if (k === null) return null;
        if (k <= 1) {
          void qc.invalidateQueries();
          return 0;
        }
        return k - 1;
      });
    }, 1000);
    return () => clearInterval(s);
  }, [data?.aktif, data?.gorunumId, kalan, qc]);

  if (!data?.aktif) return null;

  const dk = kalan === null ? null : Math.floor(kalan / 60);
  const sn = kalan === null ? null : kalan % 60;

  return (
    <div
      // `role="alert"` DEGIL: serit surekli duruyor ve alert her
      // yeniden cizimde okunurdu. Bir kez duyurulmasi yeterli.
      role="status"
      /*
        ================================================================
        KOYU ZEMIN — 04.09.2026'da `uyari-50`DEN DEGISTIRILDI
        ================================================================
        Cizim (`KullaniciGorunumu.dc.html`) gerekcesini kendi yaziyor:
        "paneldeki hicbir bant bu renkte degil, o yuzden 'baska bir
        moddayim' bilgisi arka planda bile kaybolmuyor".

        Olculdu ve hakli: panel amber bandi ZATEN kullaniyor — 2FA
        hatirlatmasi, log maskeleme uyarisi, yedek uyarisi. Gorunum
        seridi de amber oldugunda "sira disi bir moddayim" bilgisi
        siradan uyarilarin arasinda kayboluyordu.

        NABIZ YA DA YANIP SONME YOK, ve bu da cizimden: sekiz saat
        bakilabilecek bir uyari surekli hareket ederse ilk yarim saatte
        gorunmez olur.
      */
      className="bg-notr-800 sticky top-0 z-40 flex flex-wrap items-center gap-x-3.5 gap-y-2 px-6 py-3"
    >
      {/*
        ================================================================
        AD YETMIYORDU — KIMIN PROFILINDE OLDUGUNU **VE** ROLUNU SOYLE
        ================================================================
        Serit once "Başkasının gözünden bakıyorsunuz" diyor, adi ise
        asagidaki guvenlik cumlesinin BASINA ilistiriyordu. Iki sorun
        vardi:

        1. Cumle "kimin" sorusunu cevaplamiyordu; ad ayri bir yerde,
           bir uyari cumlesinin icinde duruyordu.
        2. Rol HIC yoktu. Ad tek basina "hangi gozle bakiyorum"
           sorusunu cevaplamiyor: bir yoneticinin ekraniyla bir
           uzmanin ekrani cok farkli sey gosteriyor ama ikisi de ayni
           bicimde bir insan adiyla aniliyordu — ve bu, seridin var
           olma sebebi olan YANLIS OKUMA riskinin ta kendisi.

        Cumle artik Emre'nin istedigi netlikte: kimin profilinde
        olundugu ve o kisinin rolu.
      */}
      <span className="flex min-w-0 items-center gap-2.5">
        <Ikon ad="gorunum" boyut="kucuk" className="text-notr-0 shrink-0" />
        <span className="text-notr-0 text-sm font-semibold">
          Şu an <strong className="font-semibold">{data.hedefAd}</strong> kullanıcısının profilini
          görüntülüyorsunuz
          {/*
            ROL YOKSA SATIR HIC CIZILMIYOR — "Rolsüz" DE YAZMIYOR.
            Sunucu rolu olmayan kullanici icin `null` donuyor (rol
            atamasinin suresi dolmus ya da rol pasiflestirilmis
            olabilir). Yer tutucu bir ad uydurmak, olmayan bir olcumu
            varmis gibi gostermek olurdu — ayni gerekce bu depoda
            `hazirlayan` ve `sonRevizyonGerekcesi.yazan` alanlarinda
            da yazili.

            `undefined` (sunucu soylemedi) ile `null` (rolu yok) burada
            AYNI sonuca cikiyor ve bu dogru: ikisi de "yazacak bir rol
            bilmiyorum" demek. Fark yalnizca bir IDDIA uretilecekse
            onemli olurdu; burada uretilmiyor.
          */}
          {data.hedefRol ? (
            <span className="text-notr-300 font-normal"> — {data.hedefRol}</span>
          ) : null}
        </span>
      </span>

      <span aria-hidden="true" className="bg-notr-0/25 hidden h-[18px] w-px sm:block" />

      {/*
        BILDIRIM ROZETI 05.09.2026'DA KALDIRILDI.

        Burada "Bildirim gönderilemedi" rozeti vardi ve dogru bir isi
        yapiyordu: sunucu bildirimi gonderemediginde yetkiliyi
        uyariyordu. Emre bildirimi TAMAMEN kaldirdi ("bildirim gitmesin
        erisim olunca hic kimseye"), yani gonderilecek bir sey yok.

        Rozeti birakmak, olmayan bir sey icin durum bildirmek olurdu —
        "gonderilemedi" diyen bir ekran, gonderilmeye CALISILDIGI
        izlenimi verir. Yanlis durum bildirmekten farksiz.

        Seridin kendisi duruyor ve artik daha da onemli: hedefin haberi
        olmadigi icin gorunum modunu hatirlatan tek sey o.
      */}

      <span className="text-notr-300 min-w-0 flex-1 text-sm">
        {/*
          ================================================================
          BU CUMLE SILINMEZ — GUVENLIK BILGISI, SUS DEGIL
          ================================================================
          Hedefin adi artik YUKARIDAKI baslikta; burada TEKRARLANMIYOR,
          cunku ayni adi iki kez yazmak cumleyi uzatir ve hicbir sey
          eklemez.

          Geri kalani AYNEN duruyor: "kritik islemler kapali" ve
          "yaptiginiz her islem sizin adiniza kaydediliyor". Ikisi de
          bir SONUCU haber veriyor — birincisi neden bir seyin
          calismadigini, ikincisi denetim kaydinda kimin gorunecegini.
          Basliga rol eklerken bunlari kirpmak, banti bir etikete
          indirger.

          "sizin adınıza" deniyor cunku kendi adimiz bu uctan GELMIYOR;
          adi uydurmak yerine cumle ayni seyi soyluyor.
        */}
        bu sırada kritik işlemler kapalı ve yaptığınız her işlem{" "}
        <strong className="text-notr-0 font-semibold">sizin adınıza</strong> kaydediliyor
      </span>

      {/* Geri sayim ekran okuyucuya OKUNMUYOR: saniyede bir degisen
          bir sayiyi duyurmak ekrani kullanilamaz hale getirirdi.
          Cikis dugmesi her an erisilebilir.

          SURESIZ GORUNUMDE SAYAC YERINE "süresiz" YAZIYOR ve bu satir
          `aria-hidden` DEGIL: degismeyen bir kelime ekran okuyucuyu
          bogmaz, ve "bu mod kendiliginden kapanmayacak" bilgisi
          gorenle gormeyen icin ayni derecede onemli. */}
      {kalan === null ? (
        <span className="text-notr-300 flex shrink-0 items-center gap-1.5 text-[13px]">
          <Ikon ad="bekliyor" boyut="kucuk" />
          süresiz
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="text-notr-300 flex shrink-0 items-center gap-1.5 text-[13px] tabular-nums"
        >
          <Ikon ad="bekliyor" boyut="kucuk" />
          {dk}:{String(sn).padStart(2, "0")}
        </span>
      )}

      {/*
        DUGME BEYAZ ZEMINLI: koyu seridin uzerinde `Buton`un ikincil
        gorunumu (beyaz zemin + gri halka) kayboluyordu. Cizim de beyaz
        dolu dugme veriyor — cikis yolu her zaman en gorunur oge olmali.
      */}
      <button
        type="button"
        onClick={() => cik.mutate()}
        disabled={cik.isPending}
        className={cn(
          "min-h-dokunma rounded-alan inline-flex shrink-0 items-center justify-center px-4",
          "bg-notr-0 text-notr-900 text-sm font-semibold",
          "ease-cikis hover:bg-notr-200 transition-colors duration-(--sure-anlik)",
          "focus-visible:outline-notr-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:text-notr-600 disabled:cursor-not-allowed",
        )}
      >
        {cik.isPending ? "Çıkılıyor…" : "Görünümden çık"}
      </button>
    </div>
  );
}

/** Çizimdeki madde: 5px nokta + 13px metin. */
function Madde({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-start gap-2">
      <span aria-hidden="true" className="rounded-rozet bg-notr-400 mt-[7px] size-[5px] shrink-0" />
      <span className="text-notr-600 text-[13px] leading-[1.5]">{children}</span>
    </span>
  );
}

// =====================================================================
// GECIS EKRANI
// =====================================================================

export function Gorunum() {
  const qc = useQueryClient();
  const [arama, setArama] = useState("");
  const [secili, setSecili] = useState<Aday | null>(null);
  const [gerekce, setGerekce] = useState("");

  const { data: durum } = useGorunumDurumu();

  /**
   * `sistem.gorunum.gec` sudo listesinde, yani ADAY LISTESI bile
   * sifre dogrulamasi istiyor. Bu bilincli: liste, kimin hangi rolde
   * oldugunu gosteriyor ve gorunume gecisin ilk adimi.
   */
  const { hataYakala, kapi } = useSudoKapisi(
    "Bir kullanıcının görünümüne geçmek hassas bir işlem; kimliğinizi yeniden doğrulayın.",
  );

  // Pencere kapaliysa sifre ONCE sorulur, aday sorgusu bos yere 403 atmaz (UI/UX Dalga 2 M6; 9a olcumu: bu ekran
  // `useSudoOnce`i kullanmiyordu, her aciliste `/auth/gorunum/adaylar` 403 SUDO_GEREKLI gurultusu uretiyordu).
  const { sudoHazir, onKapi } = useSudoOnce(
    "Bir kullanıcının görünümüne geçmek hassas bir işlem; kimliğinizi yeniden doğrulayın.",
  );

  const liste = useQuery<Aday[]>({
    queryKey: ["gorunum-adaylar", arama],
    queryFn: () => gorunumAdaylari(arama),
    retry: false,
    enabled: !durum?.aktif && sudoHazir,
  });

  // Sudo penceresi kapaliysa liste 403 doner; kapiyi ACAN da bu.
  useEffect(() => {
    if (liste.isError) hataYakala(() => void liste.refetch())(liste.error);
  }, [liste.isError, liste.error, liste, hataYakala]);

  const gec = useMutation({
    mutationFn: gorunumeGec,
    onSuccess: () => {
      setSecili(null);
      setGerekce("");
      /*
        `bildirildi` BURADA SAKLANMIYOR: `gorunumDurumu` onu zaten
        tasiyor ve asagidaki tazeleme onu getiriyor. Iki yerde tutmak,
        ikisi ayristigi gun hangisinin dogru oldugunu bilinmez yapardi.
      */
      void qc.invalidateQueries();
    },
    onError: hataYakala(() => {
      if (secili) gec.mutate({ hedefId: secili.id, gerekce });
    }),
  });

  if (durum?.aktif) {
    return (
      <Kart baslik="Görünüm modu açık">
        {/*
          ================================================================
          BILDIRIM GITMEDIYSE SOYLENIYOR — F-AUTH-07
          ================================================================
          (ESKIMIS YORUM — 05.09.2026'da bildirim kaldirildi.) Sunucu bildirimi
          `try/catch` icinde gonderiyor ve kanal cokerse gorunum YINE
          aciliyor — bu dogru karar, bildirim kanali coktu diye destek
          isini durdurmak yanlis olurdu. Ama o zaman verilen soz
          TUTULMAMIS oluyor ve kullanici bunu bilmiyordu: hedefin
          haberdar oldugunu sanan bir yetkili, aslinda HABERSIZ birinin
          hesabinda geziyordu.

          BILDIRIM 05.09.2026'DA TAMAMEN KALDIRILDI (Emre karari).
          Buradaki "hedefe bildirim gonderilemedi" uyarisi da kalkti:
          gonderilecek bir sey olmadigi icin bildirilecek bir durum da
          yok. Gerekce ve serit metni asagida duruyor.
        */}

        <p className="text-notr-700 text-sm">
          Şu anda <strong>{durum.hedefAd}</strong> kullanıcısının görünümündesiniz. Başka bir
          görünüme geçmek için önce kendi hesabınıza dönün — sayfanın üstündeki şeritten.
        </p>
        <p className="text-notr-600 mt-2 text-sm">Gerekçe: {durum.gerekce}</p>
      </Kart>
    );
  }

  if (onKapi) return <>{onKapi}</>;

  return (
    <div className="flex flex-col gap-4">
      {kapi}

      <Kart
        baslik="Kullanıcı görünümüne geç"
        aciklama="Destek amacıyla bir kullanıcının gördüğü ekranları görürsünüz."
      >
        {/*
          ================================================================
          "GECTIGINIZDE NE OLACAK" — HER MADDESI OLCULDU
          ================================================================
          Cizim bu kutuyu "Geçtiğinizde ne olacak" diye adlandiriyor ve
          uc madde yaziyor. Ucunu de `gorunum.service.ts` uzerinden tek
          tek izledim; ikisi aynen dogru, biri KOSULLU cikti ve cumlesi
          ona gore duzeltildi.
        */}
        <div className="rounded-alan bg-notr-50 flex flex-col gap-2 px-4 py-3.5">
          <span className="text-notr-800 text-[13px] font-semibold">Geçtiğinizde ne olacak</span>

          {/*
            SURE SAYISI SOZLESMEDEN. Sabah bu satirda sayi HIC YOKTU,
            cunku `GORUNUM_AZAMI_DAKIKA` yalnizca `apps/api` icindeydi ve
            panele yazmak onu KOPYALAMAK olurdu — bu projede tam olarak o
            hata yasandi ("şifremi unuttum" 7 gün diyordu, sunucu 30
            dakikaydi).

            Sabit `@koclukpanel/contracts`a tasindi ve sunucu da oradan
            okuyor: tek gercek. Sayi o yuzden geri geldi.
          */}
          <Madde>
            <strong className="text-notr-800">Süre sınırı yok</strong> — görünüm kendiliğinden
            kapanmaz, yalnızca siz kapatırsınız. Üstteki şerit siz çıkana kadar her ekranda durur.
          </Madde>

          {/*
            ================================================================
            "HEDEFE BILDIRIM GIDER" MADDESI KALDIRILDI — 05.09.2026
            ================================================================
            Burada "Hedef kullaniciya bildirim gonderilir — gerekceniz de
            yazilir. Gizli bir mod degildir." yaziyordu. Emre bildirimi
            kaldirdi ("bildirim gitmesin erisim olunca hic kimseye"), yani
            cumlenin ikisi de artik yanlis: bildirim gitmiyor VE mod
            hedef acisindan gercekten gizli.

            Yerine dogru olani yaziliyor. Yetkilinin bunu bilmesi
            ONEMLI: hedefin haberi olacagini sanarak davranan biri,
            "nasilsa gorecek" diye daha rahat davranir.
          */}
          <Madde>
            <strong className="text-notr-800">Hedef kullanıcı bilgilendirilmez</strong> — ona
            bildirim gitmez. Bu erişimin tek dış kaydı denetim günlüğüdür ve o kayıt silinemez.
          </Madde>

          <Madde>
            Yaptığınız her işlem denetim kaydına{" "}
            <strong className="text-notr-800">sizin kimliğinizle</strong> yazılır, hedefin adına
            değil.
          </Madde>

          <Madde>
            Kritik işlemler kapalıdır: korumalı not, 2FA değişikliği, ödeme, rol atama, dışa
            aktarma.
          </Madde>
        </div>
      </Kart>

      {secili ? (
        <Kart baslik={`${secili.ad} — gerekçe`}>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              gec.mutate({ hedefId: secili.id, gerekce });
            }}
          >
            <MetinAlani
              etiket="Neden bu hesaba bakıyorsunuz? (en az 10 karakter)"
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
              rows={3}
              placeholder="Örn. Öğrenci program ekranının açılmadığını bildirdi, aynı ekranı görmem gerekiyor."
            />

            {gec.isError && (
              <p className="text-hata-500 text-sm">
                {gec.error instanceof ApiHatasi ? gec.error.message : "Görünüme geçilemedi."}
              </p>
            )}

            <div className="flex gap-2">
              <Buton type="submit" disabled={gec.isPending || gerekce.trim().length < 10}>
                {gec.isPending ? "Geçiliyor…" : "Görünüme geç"}
              </Buton>
              <Buton gorunum="ikincil" type="button" onClick={() => setSecili(null)}>
                Vazgeç
              </Buton>
            </div>
          </form>
        </Kart>
      ) : (
        <Kart baslik="Kişi seç">
          <Alan
            etiket="Ara"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            placeholder="Ad veya soyad"
          />

          <div className="mt-3">
            {liste.isPending ? (
              <Iskelet satir={4} />
            ) : liste.isError ? (
              <p className="text-notr-600 text-sm">
                Liste yüklenemedi. Şifrenizi doğruladıktan sonra tekrar denenecek.
              </p>
            ) : liste.data.length === 0 ? (
              <BosDurum baslik="Kimse bulunamadı" aciklama="Arama terimini değiştirin." />
            ) : (
              <ul className="flex flex-col gap-2">
                {liste.data.map((a) => (
                  <li key={a.id}>
                    <SatirDugmesi onClick={() => setSecili(a)}>
                      <SatirMetni baslik={a.ad} altBaslik={a.roller.join(", ") || "Rolsüz"} />
                    </SatirDugmesi>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Kart>
      )}
    </div>
  );
}
